import {
  FormDatePicker,
  FormInput,
  FormSelect,
  FormTextarea
} from "@/components";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import { toYMD, yearOptions } from "@/lib/utils";
import { X } from "lucide-react";
import { FC, useEffect } from "react";
import { FormProvider } from "react-hook-form";
import {
  PDC_AMOUNT,
  PDC_AMOUNT_RECEIVED,
  PDC_CLEARED_DATE,
  PDC_DATE,
  PDC_NO,
  PDC_REMARKS,
  PDC_STATUS,
  pdcStatusOptions,
  YEAR,
} from "./consts";
import { UpdatePdcProps } from "./types";
import useUpdatePdc from "./useUpdatePdc";

const UpdatePdc: FC<UpdatePdcProps> = ({ onCancel, open, editData }) => {
  const { form, onSubmit, requiredFields, isMobile } = useUpdatePdc({
    onCancel,
    editData
  });

  const { errors } = form.formState;

  return (
    <Drawer open={open} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">
              {editData ? "Update" : "Add"} PDC Details
            </DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="relative bg-white rounded-lg pt-[30px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[130px] px-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[25px]">
                <FormSelect
                  name={YEAR}
                  control={form.control}
                  label="Year"
                  placeholder="Please select"
                  error={errors[YEAR]?.message}
                  isRequired={requiredFields.includes(YEAR)}
                  options={yearOptions()}
                />
                <FormInput
                  name={PDC_NO}
                  control={form.control}
                  label="PDC #"
                  placeholder="Please enter"
                  error={errors[PDC_NO]?.message}
                  isRequired={requiredFields.includes(PDC_NO)}
                  variant="integer"
                />
                <FormInput
                  name={PDC_AMOUNT}
                  control={form.control}
                  label="PDC Amount"
                  placeholder="Please enter"
                  error={errors[PDC_AMOUNT]?.message}
                  isRequired={requiredFields.includes(PDC_AMOUNT)}
                  variant="float"
                />
                <FormDatePicker
                  name={PDC_DATE}
                  control={form.control}
                  label="PDC Date"
                  placeholder="Please select"
                  error={errors[PDC_DATE]?.message}
                  isRequired={requiredFields.includes(PDC_DATE)}
                />
                <FormSelect
                  name={PDC_STATUS}
                  control={form.control}
                  label="PDC Status"
                  placeholder="Please select"
                  error={errors[PDC_STATUS]?.message}
                  isRequired={requiredFields.includes(PDC_STATUS)}
                  options={pdcStatusOptions}
                />
                <FormInput
                  name={PDC_AMOUNT_RECEIVED}
                  control={form.control}
                  label="PDC Amount Received"
                  placeholder="Please enter"
                  error={errors[PDC_AMOUNT_RECEIVED]?.message}
                  isRequired={requiredFields.includes(PDC_AMOUNT_RECEIVED)}
                  variant="float"
                />
                <FormDatePicker
                  name={PDC_CLEARED_DATE}
                  control={form.control}
                  label="PDC Cleared Date"
                  placeholder="Please select"
                  error={errors[PDC_CLEARED_DATE]?.message}
                  isRequired={requiredFields.includes(PDC_CLEARED_DATE)}
                />
                <div className="md:col-span-2">
                  <FormTextarea
                    name={PDC_REMARKS}
                    control={form.control}
                    label="PDC Remarks"
                    placeholder="Please enter"
                    error={errors[PDC_REMARKS]?.message}
                    isRequired={requiredFields.includes(PDC_REMARKS)}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-3 absolute bottom-3 w-full bg-white justify-end pb-[25px] z-[3]">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit">{editData ? "Update" : "Submit"}</Button>
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default UpdatePdc;
